import {
  IconAutomation,
  IconCalendarClock,
  IconFolders,
  IconLayout2,
  IconMessage2,
  IconMessages,
  IconPlugConnectedX,
  IconReportAnalytics,
  IconTopologyStar3,
  IconWebhook,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import { type TFunction } from "i18next";
import { type IconName } from "#app/components/ui/icons/name";
import { type useUser } from "#app/utils/user.js";

const NAV_KEYS = {
  Chat: "chat",
  "My Operators": "myOperators",
  Schedule: "schedule",
  Workbench: "workbench",
  Integrations: "integrations",
  Webhooks: "webhooks",
  Policies: "policies",
  Community: "community",
  Marketplace: "marketplace",
  Analytics: "analytics",
  "API Keys": "apiKeys",
  Billing: "billing",
  Profile: "profile",
  "Sign Out": "signOut",
} as const;

/**
 * Sidebar navigation. Order matters: the expanded sidebar renders the first
 * `PRIMARY_NAV_COUNT` general entries inline and folds the rest into the "More"
 * dropdown, so Chat / My Operators / Workbench lead.
 *
 * `icon` is the sprite name (mobile sheet, legacy surfaces); `tablerIcon` is what
 * the new sidebar renders.
 */
export default (user: ReturnType<typeof useUser>, t: TFunction<"nav">) =>
  [
    {
      section: "general",
      to: "/u/alpha/agent",
      title: t("New Chat"),
      icon: "message-square" as IconName,
      tablerIcon: IconMessage2,
    },
    {
      section: "general",
      to: "/u/alpha/my-agents",
      title: t("myOperators"),
      icon: "bot" as IconName,
      tablerIcon: IconAutomation,
    },
    {
      section: "general",
      to: "/u/human-review",
      title: t("workbench"),
      icon: "brain" as IconName,
      tablerIcon: IconTopologyStar3,
    },
    {
      // AFTER human-review on purpose. PRIMARY_NAV_COUNT is 3, so the first
      // three general entries render inline and the rest fold into "More" --
      // inserting above this line pushed Workbench out of the primary rail.
      section: "general",
      to: "/u/projects",
      title: t("projects"),
      icon: "layers" as IconName,
      tablerIcon: IconFolders,
    },
    {
      section: "general",
      to: "/u/schedule",
      title: t("schedule"),
      icon: "calendar-clock" as IconName,
      tablerIcon: IconCalendarClock,
    },
    {
      section: "general",
      to: "/u/integrations",
      title: t("integrations"),
      icon: "waypoints" as IconName,
      tablerIcon: IconPlugConnectedX,
    },
    {
      section: "general",
      to: "/u/webhooks",
      title: t("webhooks"),
      icon: "cable" as IconName,
      tablerIcon: IconWebhook,
    },
    {
      section: "general",
      to: "/u/policies",
      title: t("policies"),
      icon: "file-text" as IconName,
      badge: "Alpha",
    },
    {
      section: "general",
      to: "/u/community",
      title: t("community"),
      icon: "messages-square" as IconName,
      tablerIcon: IconMessages,
    },
    {
      section: "general",
      to: "/u/marketplace",
      title: t("marketplace"),
      icon: "store" as IconName,
      tablerIcon: IconLayout2,
    },
    {
      section: "general",
      to: "/u/dashboard",
      title: t("analytics"),
      icon: "dashboard" as IconName,
      tablerIcon: IconReportAnalytics,
    },
    {
      section: "account",
      to: "/u/api-keys",
      title: t("apiKeys"),
      icon: "book-key" as IconName,
    },
    {
      section: "account",
      to: "/u/billing",
      title: t("billing"),
      icon: "credit-card" as IconName,
    },
    {
      section: "account",
      to: `/users/${user.username}`,
      title: t("profile"),
      icon: "user" as IconName,
    },
    {
      section: "account",
      title: t("signOut"),
      icon: "log-out" as IconName,
      to: "/u/log-out",
    },
  ] as {
    section: string;
    to: string;
    title: string;
    icon: IconName;
    badge?: string;
    tablerIcon?: TablerIcon;
    onClick?: () => void;
  }[];

/** General entries shown inline in the sidebar before the "More" dropdown. */
export const PRIMARY_NAV_COUNT = 3;

export { NAV_KEYS };
